import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Dropdown, FormControl, InputGroup, Spinner } from 'react-bootstrap';
import { useText } from '../context/TextContext';
import { AdvancedButton } from './AdvancedButton';

const DEFAULT_MIN_CHARACTERS = 3;
let searchTimeot: NodeJS.Timeout | null = null;

export default function SearchInput<T>(props: {
    searchCallback: (query: string) => Promise<T[]>
    renderCandidate: (candidate: T) => React.ReactNode;
    handleSelect: (candidate: T) => Promise<void>;
    cancelable?: boolean;
    resetOnBlur?: boolean;
    noSpinner?: boolean;
    onCancel: () => void;
    className?: string;
    placeHolder?: string;
    renderEmpty?: () => React.ReactNode;
    renderFooter?: () => React.ReactNode;
    onChange?: (query: string) => void;
    minCharacters?: number;
    instantSearch?: boolean;
    autoFocus?: boolean;
    size?: 'sm' | 'lg';
    extraAction?: React.ReactNode;
}) {
    const [query, setQuery] = useState('');
    const [candidates, setCandidates] = useState<T[]>([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [activeIx, setActiveIx] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);
    const text = useText();
    const minCharacters = useMemo(() => {
        if (props.minCharacters === undefined) {
            return DEFAULT_MIN_CHARACTERS;
        }
        return props.minCharacters;
    }, [props.minCharacters]);


    useEffect(() => {
        if (props.autoFocus && inputRef.current) {
            inputRef.current.focus();
        }
    }, [props.autoFocus])

    useEffect(() => {
        if (query.length < minCharacters) {
            setCandidates([]);
            setActiveIx(0);
            setShowDropdown(false);
            return;
        }

        const fetchSuggestionsAsync = async () => {
            setProcessing(true);

            const suggestions = await props.searchCallback(query);

            setCandidates(suggestions);
            setActiveIx(0);
            setShowDropdown(document.activeElement === inputRef.current);
            setProcessing(false);
        };

        if (searchTimeot) {
            clearTimeout(searchTimeot);
        }

        if (props.instantSearch) {
            // Execute search immediately for instant search
            fetchSuggestionsAsync();
        } else {
            // Use timeout for regular search
            searchTimeot = setTimeout(() => {
                fetchSuggestionsAsync();
            }, 500);
        }
    }, [query, props, minCharacters])

    const handleSelect = async (candidate: T) => {
        setShowDropdown(false);
        if (props.resetOnBlur) {
            updateQuery('');
        }
        inputRef.current?.blur();

        setProcessing(true);
        await props.handleSelect(candidate);
        setProcessing(false);
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowDown' && candidates.length > 0) {
            e.preventDefault();
            setShowDropdown(true);
            setActiveIx(ix => Math.min(ix + 1, candidates.length - 1));
        } else if (e.key === 'ArrowUp' && candidates.length > 0) {
            e.preventDefault();
            setActiveIx(ix => Math.max(ix - 1, 0));
        } else if (e.key === 'Enter' && showDropdown && candidates[activeIx]) {
            e.preventDefault();
            handleSelect(candidates[activeIx]);
        } else if (e.key === 'Escape') {
            setShowDropdown(false);
            setActiveIx(-1);
        }
    };

    const handleCancel = () => {
        updateQuery('');
        props.onCancel();
    }

    const updateQuery = (newQuery: string) => {
        setQuery(newQuery);
        props.onChange && props.onChange(newQuery);
    }

    return <div className={`position-relative ${props.className}`}>
        <InputGroup size={props.size}>
            <InputGroup.Text className='bi bi-search ' />
            <FormControl
                ref={inputRef}
                type="text"
                value={query}
                placeholder={props.placeHolder}
                onChange={(e) => updateQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={() => {
                    if (props.resetOnBlur) {
                        updateQuery('');
                    }
                    setTimeout(() => setShowDropdown(false), 300); // Delay to allow click on dropdown item
                }}
                onFocus={() => {
                    if (query.length >= minCharacters) {
                        setActiveIx(0);
                        setShowDropdown(true);
                    }
                }}
                onClick={() => {
                    if (!showDropdown && query.length >= minCharacters) {
                        setActiveIx(0);
                        setShowDropdown(true);
                    }
                }}
                size={props.size}
            />
            {processing && !props.noSpinner && (
                <InputGroup.Text className='bg-transparent'>
                    <Spinner animation="border" size="sm" />
                </InputGroup.Text>
            )}
            {props.extraAction}
            {props.cancelable && <AdvancedButton onClick={handleCancel} variant='secondary' size={props.size}>{text.cancel}</AdvancedButton>}
        </InputGroup>


        {showDropdown && (
            <Dropdown.Menu
                show
                className="w-100 mt-1 p-0 shadow border postion-fixed"

                onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
            >
                {candidates.map((candidate, ix) => (
                    <Dropdown.Item key={ix} active={ix === activeIx} onClick={() => handleSelect(candidate)}>
                        <div className='overflow-hidden'>
                            {props.renderCandidate(candidate)}
                        </div>
                    </Dropdown.Item>
                ))}

                {candidates.length === 0 && (props.renderEmpty ? props.renderEmpty() : <Dropdown.Item disabled>{text.noResults}</Dropdown.Item>)}

                {props.renderFooter && props.renderFooter()}
            </Dropdown.Menu>
        )}
    </div>
}